/**
 * ModuleBanner context — lets a module ask a shared core screen (Setup, Reports)
 * to draw its header as that module's banner.
 *
 * The core screens are used by many modules. Restyling them outright would
 * change every one of those; this context is off by default, so only the module
 * that wraps its route in <ModuleBannerProvider> sees the banner, and every
 * other caller renders exactly as before.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { MODULE_HEADER_BG } from '@/theme/moduleHeader';

export interface ModuleBanner {
  /** The banner's background, e.g. a two-stop violet gradient. */
  background: string;
  /** Title shown in place of the screen's own, when given. */
  title?: string;
  /**
   * The module's UDA picker, drawn in the banner's control group (Setup, Reports
   * list, report viewer). When present the report viewer drops its own
   * route-switched picker, so a page never shows two.
   */
  udaSlot?: ReactNode;
  /**
   * The report screens (Reports list, report viewer) draw the UDA picker the report
   * data obeys (see ReportRouteUdaPicker) — udaSlot does not apply there. Set this
   * for a module whose reports are not UDA-scoped, to leave the picker off.
   */
  noUda?: boolean;
}

const ModuleBannerContext = createContext<ModuleBanner | null>(null);

export function ModuleBannerProvider({ value, children }: { value: ModuleBanner; children: ReactNode }) {
  return <ModuleBannerContext.Provider value={value}>{children}</ModuleBannerContext.Provider>;
}

/** Null unless a module asked for its banner. */
export function useModuleBanner(): ModuleBanner | null {
  return useContext(ModuleBannerContext);
}

/** The Finance module's banner — the shared module-header orange, as Finance2Shell's heroBanner. */
export const FINANCE_BANNER: ModuleBanner = {
  background: MODULE_HEADER_BG,
};
